import { useCallback, useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";

import { businessApi } from "../../../../services/api";
import { Button, ErrorState, SkeletonRows, Surface, plainError } from "../../../../components/ui";
import PayRuleApplicabilityList from "./PayRuleApplicabilityList";
import SettingsRelatedLinks from "./SettingsRelatedLinks";

// Report 58 (§G): one employee's answer for overtime and rest-day pay, night shift differential and holiday pay — the
// business's facts, the employee's own exception or holiday-pay handling, and what payroll does. Worked out by the server
// with the same rules payroll uses (GET …/employees/:id/pay-rule-applicability); the panels below change the facts.

const longDate = (iso) => new Date(`${iso}T00:00:00Z`).toLocaleDateString("en-PH", { timeZone: "UTC", month: "long", day: "numeric", year: "numeric" });

export default function PayRuleApplicabilityPanel({ memberId, memberName, refreshKey = 0 }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await businessApi.getHRPayRuleApplicability(memberId));
      setError("");
    } catch (err) {
      setError(plainError(err, "This employee's pay rules couldn't be loaded."));
    } finally {
      setLoading(false);
    }
  }, [memberId]);
  useEffect(() => { load(); }, [load, refreshKey]);

  if (error && !data) return <ErrorState title="Pay rules couldn't be loaded" description={error} onRetry={load} />;
  if (!data) return <SkeletonRows rows={3} columns={2} label="Loading this employee's pay rules" />;

  return (
    <Surface padded as="section" aria-label="Pay rules for this employee">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
        <h2 className="text-section-title text-foreground">Pay rules for {memberName || "this employee"}</h2>
        <Button size="sm" variant="quiet" icon={RefreshCw} loading={loading} onClick={load}>Check again</Button>
      </div>
      <p className="mt-0.5 max-w-[75ch] text-body-sm text-secondary">
        As of {longDate(data.date)}: whether the law requires each benefit for this employee, and what payroll does. It
        combines the business size, any exception approved for this employee, and how their holiday pay is handled below.
      </p>
      <PayRuleApplicabilityList rules={data.rules || []} className="mt-2" />
      <SettingsRelatedLinks items={[{ to: "/business/hr/settings/pay-rules", label: "See the business size and rule sources" }]} />
    </Surface>
  );
}
